import type { DynamicEventsResponse, FunnelEvents } from "@/api/insights";
import type {
  GetEventDefsLibraryApiRequest,
  GetEventDefsLibraryResponse,
} from "@/LibraryPageV2/api";
import type { LoadedEventDefinition } from "@/types/global";
import type { EventSource, EventType } from "@/eventTypes";

import { getPreviewGraph } from "../graph";
import { EVENT_DEF_IDS } from "../graph/entities";
import type { PreviewEventDef } from "../graph/types";
import { toLoadedDestinationInfo } from "./destinations";

export function handleGetEventDefsEnriched(_params: {}): FunnelEvents[] {
  const graph = getPreviewGraph();
  return [...graph.eventDefs].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
}

export function handleGetDynamicEvents(_params: {}): DynamicEventsResponse {
  const graph = getPreviewGraph();
  return {
    midFunnel: graph.eventDefs.filter((e) => e.funnelStage === "MidFunnel"),
    bottomFunnel: graph.eventDefs.filter(
      (e) => e.funnelStage === "BottomFunnel",
    ),
  };
}

// Auto-captured events read as autotracked; everything else as manually
// tracked. Type drives the Event Library's "Type" column + filter.
const LIBRARY_TYPE_BY_ID: Record<string, EventType> = {
  [EVENT_DEF_IDS.page_view]: "pageview",
  [EVENT_DEF_IDS.click]: "click",
  [EVENT_DEF_IDS.form_started]: "manual_tracked",
  [EVENT_DEF_IDS.form_submitted]: "submit",
  [EVENT_DEF_IDS.appointment_booking_started]: "manual_tracked",
  [EVENT_DEF_IDS.appointment_booking_completed]: "manual_tracked",
  [EVENT_DEF_IDS.call_initiated]: "manual_tracked",
};

// The set of destinations every synthetic event delivers to: the ones that
// accrue delivery metrics (search & social + display). Audiences sync cohorts,
// not events, so they're excluded — keeping the Library "sent to" icons aligned
// with where delivery metrics actually exist.
function enabledDeliveryDests(): Record<string, boolean> {
  const graph = getPreviewGraph();
  return Object.fromEntries(
    graph.destinations
      .filter((d) => d.hasDeliveryMetrics)
      .map((d) => [d.id, true]),
  );
}

function toLibraryEventDef(
  e: PreviewEventDef,
  enabledDests: Record<string, boolean>,
): LoadedEventDefinition {
  const isAuto = e.tags["Auto Event"] === true;
  return {
    id: e.eventDefId,
    name: e.name,
    type: LIBRARY_TYPE_BY_ID[e.eventDefId] ?? "manual_tracked",
    source: (isAuto ? "autotrack" : "manual_tracked") as EventSource,
    tags: e.tags,
    description: e.description,
    is_hidden: e.isHidden,
    segment_compatible: false,
    filter: { type: "and", filters: [] },
    snapshots: [],
    enabled_dests: enabledDests,
    enabled_client_destinations: {},
    enabled_server_destinations: enabledDests,
  };
}

export function handleGetEventDefsLibrary(
  req: GetEventDefsLibraryApiRequest,
): GetEventDefsLibraryResponse {
  const graph = getPreviewGraph();
  const enabledDests = enabledDeliveryDests();
  const all = graph.eventDefs.map((e) => toLibraryEventDef(e, enabledDests));

  let defs = all;
  if (req.search_query) {
    const q = req.search_query.toLowerCase();
    defs = defs.filter((d) => d.name.toLowerCase().includes(q));
  }
  if (req.type_filter?.length) {
    defs = defs.filter(
      (d) => d.type !== null && req.type_filter.includes(d.type),
    );
  }
  if (req.tag_ids?.length) {
    defs = defs.filter((d) => req.tag_ids.some((t) => d.tags[t]));
  }

  const dir = req.sort_direction === "desc" ? -1 : 1;
  const sortKey = (d: LoadedEventDefinition): string => {
    switch (req.sort_column) {
      case "type":
        return d.type ?? "";
      case "description":
        return d.description;
      default:
        return d.name;
    }
  };
  defs = [...defs].sort((a, b) => dir * sortKey(a).localeCompare(sortKey(b)));

  const total = defs.length;
  const offset = req.offset ?? 0;
  const limit = req.limit ?? total;

  return {
    event_defs: defs.slice(offset, offset + limit),
    total_count: total,
    all_tags: [...new Set(all.flatMap((d) => Object.keys(d.tags)))].sort(),
    all_event_types: [
      ...new Set(all.map((d) => d.type).filter((t): t is EventType => !!t)),
    ].sort(),
    dest_info: graph.destinations.map(toLoadedDestinationInfo),
  };
}

export function handleGetEventDef(req: {
  event_def_id: string;
}): LoadedEventDefinition | null {
  const graph = getPreviewGraph();
  const e = graph.eventDefs.find((d) => d.eventDefId === req.event_def_id);
  return e ? toLibraryEventDef(e, enabledDeliveryDests()) : null;
}
